/*
 * Migration Lot 4 (2026-04-30) : remplace `@mui/material/Tabs` par
 * shadcn/ui (Radix Tabs). API publique inchangée — `TabsCustom(props)`
 * passe `props` aux quatre enfants et lit `props.errors` pour afficher
 * un bandeau d'alertes entre le tablist et le panel actif.
 *
 * Cleanup : retrait du dead code lié à l'ancienne logique de permission
 * (`getUserData`, `findPermission`, `AdminRequest`, `secretairePermission`).
 * Les imports étaient utilisés uniquement pour calculer une variable
 * référencée par un `if` commenté — donc inerte côté rendu. Si la
 * permission doit revivre, la rebrancher au moment où on remet la
 * condition (pas de raison de garder du code inerte d'ici là).
 *
 * Style "underline" : voir le commentaire dans `Pharmacy/TabsIndex.js`.
 */
import * as React from "react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import TabOne from "./TabOne";
import TabTwo from "./TabTwo";
import TabThree from "./TabThree";
import TabFour from "./TabFour";
import Alert from "../../Others/Alert";

// Style underline « refined » : trigger sans fond, séparateur emerald
// quand actif, hover slate-50 pour signaler l'interactivité.
const tabsListClass =
  "bg-transparent p-0 h-auto w-full justify-start gap-1 rounded-none border-b border-slate-200 overflow-x-auto";
const tabsTriggerClass =
  "rounded-none border-b-2 border-transparent px-4 sm:px-5 py-3 -mb-px text-sm font-medium text-slate-500 " +
  "transition-colors hover:text-slate-700 hover:bg-slate-50/60 " +
  "data-[state=active]:border-emerald-600 data-[state=active]:bg-transparent " +
  "data-[state=active]:text-emerald-700 data-[state=active]:shadow-none " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200";

export default function TabsCustom(props) {
  // Default `[]` défensif : l'ancien code faisait `errors.length > 0`
  // qui crash si `errors` est undefined. Aucun consommateur connu ne
  // passe undefined aujourd'hui, mais le coût défensif est nul.
  const { errors = [] } = props;

  return (
    <Tabs defaultValue="patient" className="w-full">
      <TabsList className={tabsListClass}>
        <TabsTrigger value="patient" className={tabsTriggerClass}>
          <i className="bx bx-id-card text-base mr-1.5 align-middle" />
          Fiche patient
        </TabsTrigger>
        <TabsTrigger value="parcours" className={tabsTriggerClass}>
          <i className="bx bx-pulse text-base mr-1.5 align-middle" />
          Parcours de soins
        </TabsTrigger>
        <TabsTrigger value="bons" className={tabsTriggerClass}>
          <i className="bx bx-receipt text-base mr-1.5 align-middle" />
          Bons
        </TabsTrigger>
        <TabsTrigger value="examens" className={tabsTriggerClass}>
          <i className="bx bx-clipboard text-base mr-1.5 align-middle" />
          Demande d'Examens
        </TabsTrigger>
      </TabsList>

      {/*
        Bandeau d'erreurs rendu entre la TabsList et les TabsContent —
        Radix Tabs Root accepte n'importe quel enfant en flow normal, le
        Context interne ne s'en préoccupe pas.
      */}
      {errors.length > 0 && (
        <div className="mx-4 sm:mx-6 mt-4 w-auto space-y-2">
          {errors.map((error, idx) => (
            <Alert key={idx} message={error.message} />
          ))}
        </div>
      )}

      {/* p-4 sm:p-6 — un seul niveau de padding pour éviter le double
          encadrement avec la carte parente.

          `forceMount` : Radix démonte par défaut le panel inactif, ce
          qui réinitialise les `useState` internes de chaque Tab (form
          de consultation en cours, lignes de facture, etc.). En forçant
          le mount, l'arbre React reste vivant entre les switches.

          ⚠️ Piège Radix : avec `forceMount`, le composant rend les
          panels inactifs SANS poser l'attribut HTML `hidden` (la prop
          est `hidden={!present}` et `present = forceMount || isSelected`
          → toujours `true`). Sans le `data-[state=inactive]:hidden`
          ci-dessous, les 4 panels s'empilent visuellement. La règle CSS
          rétablit `display:none` sur les panels inactifs tout en gardant
          leur arbre React monté. */}
      <TabsContent
        value="patient"
        className="p-4 sm:p-6 data-[state=inactive]:hidden"
        forceMount
      >
        <TabOne {...props} />
      </TabsContent>
      <TabsContent
        value="parcours"
        className="p-4 sm:p-6 data-[state=inactive]:hidden"
        forceMount
      >
        <TabTwo {...props} />
      </TabsContent>
      <TabsContent
        value="bons"
        className="p-4 sm:p-6 data-[state=inactive]:hidden"
        forceMount
      >
        <TabThree {...props} />
      </TabsContent>
      <TabsContent
        value="examens"
        className="p-4 sm:p-6 data-[state=inactive]:hidden"
        forceMount
      >
        <TabFour {...props} />
      </TabsContent>
    </Tabs>
  );
}
